<template>
  <WdsStack direction-column gap-md>
    <div class="tw-flex tw-justify-between tw-items-center">
      <WdsText :sub-text="$t('Insurance coverage')" />
      <WdsText v-if="latestUpdatedAt" :text="`${$t('Updated')}: ${latestUpdatedAt}`" secondary sm />
    </div>

    <WdsStack v-if="!hasAnyInsurances" direction-column gap-sm>
      <WdsText :text="$t('No insurance details provided yet.')" secondary sm />
    </WdsStack>

    <WdsStack v-else direction-column gap-lg>
      <template v-for="insurance in insurances">
        <div
          v-if="insurance.hasInsurance"
          :key="insurance.type"
          class="tw-flex tw-justify-between tw-items-center"
        >
          <div class="tw-flex tw-flex-col tw-gap-0.5">
            <div class="tw-flex tw-items-center tw-gap-2">
              <span
                class="tw-text-lg tw-leading-none"
                :class="{
                  ['tw-text-green-500']: !proofOfInsuranceMissing(insurance),
                  ['tw-text-orange-500']: proofOfInsuranceMissing(insurance),
                }"
              >
                •
              </span>
              <WdsText :text="insurance.title" medium />
            </div>
            <WdsTooltip v-if="proofOfInsuranceMissing(insurance)" global :z-index="50">
              <template #tooltip-content>
                {{
                  $t(
                    'Insurance has been indicated, but either policy limit and/or proof of insurance is missing.',
                  )
                }}
              </template>
              <WdsStack gap-xs align-center>
                <WdsText :text="$t('Awaiting information')" secondary sm />
                <WdsIcon name="help" :size="16" />
              </WdsStack>
            </WdsTooltip>
            <WdsText
              v-if="insurance.policyLimit"
              :text="
                $t('Limit: :policyLimit', { policyLimit: formatCurrency(insurance.policyLimit) })
              "
              secondary
              sm
            />
          </div>
          <WdsButtonGhost
            v-if="hasFiles(insurance)"
            class="tw-flex-shrink-0"
            @click="downloadAllFiles(insurance)"
          >
            <div class="tw-flex tw-items-center tw-gap-1">
              <WdsIcon :name="WdsIconNameEnum.DOCUMENT" :size="16" class="tw-text-gray-600" />
              <WdsText :text="String(insurance.files?.length ?? 0)" secondary sm />
            </div>
          </WdsButtonGhost>
        </div>

        <div v-else :key="`${insurance.type}_2`" class="tw-flex tw-flex-col tw-gap-0.5">
          <div class="tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-red-500 tw-text-lg tw-leading-none">•</span>
            <WdsText :text="insurance.title" medium />
          </div>
          <WdsText :text="$t('Not insured')" secondary sm />
        </div>
      </template>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  WdsStack,
  WdsText,
  WdsButtonGhost,
  WdsIcon,
  WdsIconNameEnum,
  WdsTooltip,
} from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { toFormattedAmountByLocale } from '@/utils/currency'

interface InsuranceFile {
  id: string
  name: string
  title?: string | null
  url: string
}

interface Insurance {
  type: string
  title: string
  hasInsurance: boolean
  policyLimit?: number | null
  updatedAt?: string | null
  files: InsuranceFile[]
}

interface Props {
  insurances: Insurance[]
  currency?: string
}

const props = withDefaults(defineProps<Props>(), {
  currency: 'GBP',
})

const formatDate = (dateString: string): string => {
  if (!dateString) return ''

  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const hasAnyInsurances = computed(() => props.insurances && props.insurances.length > 0)

const latestUpdatedAt = computed(() => {
  if (!hasAnyInsurances.value) return null

  const insuranceWithDate = props.insurances.find((ins) => ins.updatedAt)
  if (!insuranceWithDate?.updatedAt) return null

  return formatDate(insuranceWithDate.updatedAt)
})

const hasFiles = (insurance: Insurance): boolean => insurance.files && insurance.files.length > 0

const downloadAllFiles = (insurance: Insurance): void => {
  if (!hasFiles(insurance)) return

  insurance.files.forEach((file) => {
    window.open(file.url, '_blank')
  })
}

const formatCurrency = (amount: number): string => {
  const formatted = toFormattedAmountByLocale(amount.toString(), undefined, true)
  return `${props.currency} ${formatted}`
}

const proofOfInsuranceMissing = (insurance: Insurance) =>
  !insurance.policyLimit || insurance.files.length === 0
</script>
